import { BaseHTMLElement } from '../BaseHTMLElement';

export class LandingPage extends BaseHTMLElement {
  private onGetStarted: () => void;

  constructor(onGetStarted: () => void) {
    super('div', 'landing-page');
    this.onGetStarted = onGetStarted;
    this.render();
  }

  private render(): void {
    this.element.innerHTML = `
      <div class="landing-container">
        <header class="landing-header" role="banner">
          <div class="logo">
            <h1>Salmon - Creative Canvas Platform</h1>
            <p>Professional design tools for the modern web</p>
          </div>
          <nav class="landing-nav" role="navigation" aria-label="Main navigation">
            <a href="#features" aria-label="View features">Features</a>
            <a href="#about" aria-label="Learn about us">About</a>
            <button class="btn-secondary" id="get-started-btn" aria-label="Get started with Salmon">Get Started</button>
          </nav>
        </header>

        <main class="landing-main" role="main">
          <section class="hero" aria-labelledby="hero-heading">
            <div class="hero-content">
              <h2 id="hero-heading">Create, Collaborate, and Share Your Creative Vision</h2>
              <p>Salmon is a powerful canvas-based platform designed for creative professionals. Design, prototype, and bring your ideas to life with our intuitive tools and real-time collaboration features. Built for the modern web with classic design principles.</p>
              <div class="hero-actions">
                <button class="btn-primary btn-large" id="hero-get-started" aria-label="Start creating with Salmon">Start Creating</button>
                <button class="btn-outline btn-large" id="learn-more" aria-label="Learn more about Salmon features">Learn More</button>
              </div>
            </div>
            <div class="hero-visual" aria-label="Salmon canvas interface preview">
              <div class="canvas-preview">
                <div class="canvas-mockup">
                  <div class="mockup-shape shape-1" aria-hidden="true"></div>
                  <div class="mockup-shape shape-2" aria-hidden="true"></div>
                  <div class="mockup-shape shape-3" aria-hidden="true"></div>
                </div>
              </div>
            </div>
          </section>

          <section class="features" id="features" aria-labelledby="features-heading">
            <div class="features-header">
              <h3 id="features-heading">Powerful Features for Creative Professionals</h3>
              <p>Everything you need to create amazing designs and collaborate with your team in the digital age</p>
            </div>
            <div class="features-grid" role="list">
              <article class="feature-card" role="listitem">
                <div class="feature-icon" aria-hidden="true">🎨</div>
                <h4>Intuitive Design Tools</h4>
                <p>Drag, drop, and create with our user-friendly interface designed for both beginners and professionals. Advanced tools for vector graphics, typography, and layout design.</p>
              </article>
              <article class="feature-card" role="listitem">
                <div class="feature-icon" aria-hidden="true">⚡</div>
                <h4>Real-time Collaboration</h4>
                <p>Work together with your team in real-time. See changes instantly, leave comments, and collaborate seamlessly on projects from anywhere in the world.</p>
              </article>
              <article class="feature-card" role="listitem">
                <div class="feature-icon" aria-hidden="true">📱</div>
                <h4>Cross-Platform Support</h4>
                <p>Access your projects from anywhere. Works perfectly on desktop, tablet, and mobile devices with responsive design and touch-friendly interfaces.</p>
              </article>
              <article class="feature-card" role="listitem">
                <div class="feature-icon" aria-hidden="true">🔒</div>
                <h4>Secure & Private</h4>
                <p>Your creative work is protected with enterprise-grade security and privacy controls. End-to-end encryption and secure cloud storage.</p>
              </article>
            </div>
          </section>

          <section class="cta" aria-labelledby="cta-heading">
            <div class="cta-content">
              <h3 id="cta-heading">Ready to Start Creating?</h3>
              <p>Join thousands of creators, designers, and creative professionals who trust Salmon for their design needs. Experience the future of web-based design tools.</p>
              <button class="btn-primary btn-large" id="cta-get-started" aria-label="Get started with Salmon for free">Get Started Free</button>
            </div>
          </section>
        </main>

        <footer class="landing-footer" role="contentinfo">
          <div class="footer-content">
            <div class="footer-section">
              <h5>Product</h5>
              <ul role="list">
                <li><a href="#features" aria-label="View all features">Features</a></li>
                <li><a href="#pricing" aria-label="View pricing plans">Pricing</a></li>
                <li><a href="#templates" aria-label="Browse design templates">Templates</a></li>
              </ul>
            </div>
            <div class="footer-section">
              <h5>Company</h5>
              <ul role="list">
                <li><a href="#about" aria-label="Learn about Salmon">About</a></li>
                <li><a href="#careers" aria-label="View career opportunities">Careers</a></li>
                <li><a href="#contact" aria-label="Contact us">Contact</a></li>
              </ul>
            </div>
            <div class="footer-section">
              <h5>Support</h5>
              <ul role="list">
                <li><a href="#help" aria-label="Get help and support">Help Center</a></li>
                <li><a href="#docs" aria-label="Read documentation">Documentation</a></li>
                <li><a href="#community" aria-label="Join our community">Community</a></li>
              </ul>
            </div>
          </div>
          <div class="footer-bottom">
            <p>&copy; 2024 Salmon. All rights reserved. | <a href="#privacy" aria-label="Privacy policy">Privacy Policy</a> | <a href="#terms" aria-label="Terms of service">Terms of Service</a></p>
          </div>
        </footer>
      </div>
    `;

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    const getStartedButtons = this.element.querySelectorAll('#get-started-btn, #hero-get-started, #cta-get-started');
    const learnMoreButton = this.element.querySelector('#learn-more') as HTMLButtonElement;

    getStartedButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.onGetStarted();
      });
    });

    learnMoreButton.addEventListener('click', () => {
      const featuresSection = this.element.querySelector('#features');
      featuresSection?.scrollIntoView({ behavior: 'smooth' });
    });

    // Smooth scrolling for navigation links
    const navLinks = this.element.querySelectorAll('.landing-nav a[href^="#"]');
    navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href')?.substring(1);
        const targetElement = document.getElementById(targetId || '');
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }
}
